import { FC, memo, useState } from 'react';

import { getHealthFrequencyValue } from '@common/calculations';
import { AddOnConfigModal, AddOnMembers } from '@healthquote/blocks';
import {
  HealthAddOn,
  HealthAddOnType,
  useHealthQuoteStore
} from '@store/healthquote';
import { Box, Grid, Checkbox, Skeleton, Text, Flex, Tooltip } from '@ui';

import { AddOnSubOptions } from './AddOnSubOptions';

interface HealthAddOnItemProps {
  addOn: HealthAddOn;
  productFinalized?: boolean;
  customNamePrefix?: string;
}

const HealthAddOnItem: FC<HealthAddOnItemProps> = memo(
  ({ addOn, productFinalized, customNamePrefix }) => {
    const {
      loadingPremium,
      toggleAddOn,
      plan,
      toggleModalAddOn,
      toggleAddOnDetail
    } = useHealthQuoteStore((state) => ({
      loadingPremium: state.loadingPremium,
      toggleAddOn: state.toggleAddOn,
      plan: state.plan,
      toggleModalAddOn: state.toggleModalAddOn,
      toggleAddOnDetail: state.toggleAddOnDetail
    }));

    const [drawerEnabled, setDrawerEnabled] = useState<boolean>(false);
    const updating = loadingPremium;

    const showAddOnOptionsDrawer = () => {
      setDrawerEnabled(true);
    };

    //To not disable addons with canSelectAlternatively key
    const disabled =
      updating ||
      (addOn.type === HealthAddOnType.MANDATORY &&
        !addOn.canSelectAlternatively?.length);

    return (
      <Box w="100%" mb={productFinalized ? 'xs' : 0}>
        <Grid align="center">
          <Grid.Col span={{ base: 7, sm: 8 }}>
            <Flex align="center">
              {!productFinalized ? (
                <Checkbox
                  disabled={disabled}
                  name={addOn.name}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked;
                    if (addOn.noDefaultOption) {
                      //Add ons with no default option
                      if (checked) {
                        showAddOnOptionsDrawer();
                      } else {
                        //Uncheck the add on along with modal values
                        toggleModalAddOn(addOn.key, plan.members, false);
                      }
                    } else {
                      //All the existing add ons
                      toggleAddOn(addOn.key, checked);
                    }
                  }}
                  checked={addOn.included}
                />
              ) : null}
              <Tooltip label="Click to view details" withArrow>
                <Flex
                  ml={!productFinalized ? '0.4rem' : 0}
                  align="center"
                  onClick={() => {
                    toggleAddOnDetail(addOn.key);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <Text lightColor="secondary" darkColor="text.2" size="sm">
                    {addOn.name}
                  </Text>
                </Flex>
              </Tooltip>
            </Flex>
          </Grid.Col>
          <Grid.Col span={{ base: 5, md: 4, sm: 4 }}>
            <Flex justify="flex-end" w="100%">
              {!updating && (addOn.additionalPremium || addOn.noDefaultOption) ? (
                <Text lightColor="text" darkColor="text.2" fw={500} ta="right">
                  {addOn.additionalPremium ? (
                    getHealthFrequencyValue(addOn.additionalPremium, 2)
                  ) : addOn.noDefaultOption ? (
                    <Text size="xs">
                      <Box
                        component="span"
                        c="#3DABF5"
                        style={{ cursor: 'pointer' }}
                        onClick={showAddOnOptionsDrawer}
                      >
                        Configure
                      </Box>
                    </Text>
                  ) : (
                    '₹0.00'
                  )}
                </Text>
              ) : (
                <Skeleton height={20} width={50} />
              )}
            </Flex>
          </Grid.Col>
        </Grid>
        <AddOnMembers
          members={plan.members}
          addOnKey={addOn.key}
          productName={plan.product}
        />
        <AddOnSubOptions
          addOn={addOn}
          namePrefix={customNamePrefix ? customNamePrefix : 'summary-'}
        />
        <AddOnConfigModal
          opened={drawerEnabled}
          onClose={() => setDrawerEnabled(false)}
          addOn={addOn}
          productName={plan.product}
        />
      </Box>
    );
  }
);

export { HealthAddOnItem };
